import { BottomSheet } from '@s4l/ds'
import { useS4LContext } from '@s4l/react-context'
import { LoginButton as LoginButtonUikit } from '@s4l/uikit'
import { useBrowser } from '@s4l/utils/hooks/useBrowser'
import { logoutRequestAction } from 'client/contexts/Login/actions'
import { SET_SHOW_LOGIN_MODAL } from 'client/contexts/Login/constants'
import { getLoginModalFrom, getShowLoginModal } from 'client/contexts/Login/selectors'
import { sendTrackingRequestAction } from 'client/contexts/Tracking/actions'
import { clickAuthentication } from 'client/contexts/Tracking/events'
import Cookies from 'js-cookie'
import noop from 'lodash/noop'
import { useRouter } from 'next/router'
import React, { MouseEvent } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { addRedirect } from 'src/comps/LoginButton/utils'
import { COOKIE_GOOGLE_ACCESS_TOKEN } from 'src/constants/cookies'
import useBus from 'src/hooks/useBus'
import { useLogin } from 'src/hooks/useLogin/useLogin'
import { usePage } from 'src/hooks/usePage'
import { useSession } from 'src/hooks/useSession'
import { useSystem } from 'src/hooks/useSystem'
import { BreakpointSizes } from 'src/types/device'
import LoginForm from '../LoginForm'
import SidebarMenu from '../SidebarMenu'

type LoginButtonProps = {
  forceFull?: boolean
  onClick?: () => void
  onClose?: () => void
}

const LoginButton = ({ forceFull, onClick = noop, onClose = noop }: LoginButtonProps) => {
  useLogin()

  const { i18n } = useS4LContext()
  const { activeBreakpoint } = useBrowser()

  const { apiHostName } = useSystem()
  const { pageId } = usePage()
  const router = useRouter()
  const dispatch = useDispatch()

  const { isLogged, customer } = useSession()

  const isModalOpen = useSelector(getShowLoginModal)
  const loginModalFrom = useSelector(getLoginModalFrom)

  const setModalOpen = (showModal: boolean, from?: string) =>
    dispatch({ type: SET_SHOW_LOGIN_MODAL, payload: { showModal, from } })

  const { subscribe } = useBus()

  subscribe('show-login-modal', payload => {
    setModalOpen(true, payload?.from)
  })

  const handleClick = () => {
    if (!isModalOpen) {
      onClick()
    }
    setModalOpen(!isModalOpen)
    dispatch(sendTrackingRequestAction({ event: clickAuthentication() }))
  }

  const handleClose = (event: MouseEvent<HTMLAnchorElement>) => {
    event?.stopPropagation()

    setModalOpen(false)
    onClose()
    dispatch(sendTrackingRequestAction({ event: clickAuthentication() }))
  }

  const handleLogOut = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    event.stopPropagation()

    setModalOpen(false)
    onClose()
    addRedirect(router, pageId)

    dispatch(logoutRequestAction({ hostname: apiHostName }))
    if (typeof window !== 'undefined' && 'ReactNativeWebView' in window) {
      const reactNativeWebView = window.ReactNativeWebView as {
        postMessage: (message: string) => void
      }
      reactNativeWebView.postMessage(
        JSON.stringify({ type: 'GOOGLE_LOGOUT_CLICK', action: 'click' })
      )
    }
  }

  return (
    <>
      <LoginButtonUikit
        onClick={handleClick}
        data-testid="header-login-button"
        variant={forceFull ? 'text-on-all-devices' : 'standard'}
        userJSON={
          isLogged && customer?.userId
            ? {
                id: customer?.userId.toString(),
                googleAccessToken: Cookies.get(COOKIE_GOOGLE_ACCESS_TOKEN),
                email: customer?.email,
                name: customer?.name,
              }
            : undefined
        }
      />
      {!isLogged && (
        <BottomSheet open={isModalOpen} onClose={() => setModalOpen(false)}>
          <BottomSheet.Content
            modalOnDesktop
            title={i18n.formatMessage({ id: 'global.login' })}
            size="s"
            scroll
          >
            <LoginForm from={loginModalFrom} />
          </BottomSheet.Content>
        </BottomSheet>
      )}

      {isLogged && (
        <BottomSheet
          direction={activeBreakpoint < BreakpointSizes.M ? 'bottom' : 'right'}
          open={isModalOpen}
          onClose={() => setModalOpen(false)}
        >
          <BottomSheet.Content
            title={i18n.formatMessage({ id: 'global.user-profile' })}
            contentPadding={false}
          >
            <SidebarMenu handleLogOut={handleLogOut} handleClose={handleClose} />
          </BottomSheet.Content>
        </BottomSheet>
      )}
    </>
  )
}

export default LoginButton
